// TeamAbsences.tsx — KaziPeople v5 design (no MUI), real data.
import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { getTeamOut, getEmployees, getDepartments } from '@/lib/hrApi';
import { useC, useDark, type C as ColorSet } from '@/features/shared/theme/kaziColors';
import { toDisplayDate } from '@/lib/orgFormat';

interface TeamMember {
  id: string;
  name: string;
  avatarUrl?: string;
  department?: string;
  absence: { type: 'vacation' | 'sick' | 'wfh' | 'away'; startDate: string; endDate?: string; status: 'approved' | 'pending' };
}

const normType = (typ?: string): TeamMember['absence']['type'] => {
  const s = (typ || '').toLowerCase();
  // The server redacts the leave type for rows outside the caller's disclosure
  // set (org-wide approver / own subtree / self) — those arrive with no type
  // and must NOT be mislabeled as vacation.
  if (!s) return 'away';
  if (s.includes('sick')) return 'sick';
  if (s.includes('wfh') || s.includes('home')) return 'wfh';
  return 'vacation';
};

// LOCAL calendar date, computed on demand — the old module-scope constant was
// (a) frozen at bundle load and (b) the UTC date, so "today" was wrong for the
// whole session west of UTC and after any midnight (timezone program 2026-08).
const localTodayIso = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const weekRange = () => {
  const today = new Date();
  const start = new Date(today); start.setDate(today.getDate() - today.getDay());
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};
const thisWeek = weekRange();

const typeColor = (C: ColorSet, t: string) =>
  (t === 'sick' ? C.peach : t === 'wfh' ? C.blue : t === 'away' ? C.charcoalLight : C.orange);
// See toDisplayDate: a date-only leave date parsed with `new Date` is UTC
// midnight, and renders as the previous day for any UTC-negative viewer.
const fmtRange = (s: string, e?: string) => {
  const opt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (!e || s === e) return toDisplayDate(s).toLocaleDateString('en-US', opt);
  return `${toDisplayDate(s).toLocaleDateString('en-US', opt)} – ${toDisplayDate(e).toLocaleDateString('en-US', opt)}`;
};

export const TeamAbsences: React.FC<{ onClose?: () => void }> = () => {
  const C = useC();
  const dark = useDark();
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0);
  const [members, setMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [out, emps, depts] = await Promise.all([
          // include_pending: approver-shaped viewers also see pending rows,
          // badged below; the server decides who qualifies.
          getTeamOut(30, true),
          getEmployees({ page: 1, page_size: 100 }).catch(() => ({ items: [] as any[] })),
          getDepartments().catch(() => ({ items: [] as any[] })),
        ]);
        const byUser = new Map<string, any>((emps.items || []).map((e: any) => [e.user_id, e]));
        const deptName = new Map<string, string>((depts.items || []).map((d: any) => [d.id, d.name]));
        setMembers((Array.isArray(out) ? out : []).map((r: any) => {
          const e = byUser.get(r.employee_id);
          return {
            id: String(r.id),
            name: e ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.email : 'Teammate',
            avatarUrl: e?.profile_picture_url || undefined,
            department: (e?.department_id && deptName.get(e.department_id)) || undefined,
            absence: { type: normType(r.type), startDate: r.start_date, endDate: r.end_date || undefined, status: String(r.status || '').toLowerCase() === 'pending' ? 'pending' : 'approved' },
          };
        }));
      } catch { /* leave service unavailable */ }
    })();
  }, []);

  const filtered = members.filter((m) => {
    const s = m.absence.startDate, e = m.absence.endDate || m.absence.startDate;
    if (activeTab === 0) return s <= localTodayIso() && e >= localTodayIso();
    if (activeTab === 1) return s <= thisWeek.end && e >= thisWeek.start;
    return true;
  });
  const countOf = (t: string) => filtered.filter((m) => m.absence.type === t).length;

  // "All" here means everything the feed covers: today through +30 days.
  const tabs = ['Today', 'This Week', 'Next 30 days'];
  const initials = (n: string) => n.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div>
      <div style={{ padding: '18px 24px', background: C.orange, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Users size={18} />
        <span style={{ fontSize: 15, fontWeight: 700 }}>Out of Office</span>
      </div>

      {/* Type counts */}
      <div style={{ padding: '14px 24px', display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}` }}>
        {(['vacation', 'sick', 'wfh', 'away'] as const)
          .filter((ty) => ty !== 'away' || countOf('away') > 0)
          .map((ty) => (
          <span key={ty} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 99, background: `${typeColor(C, ty)}22`, color: typeColor(C, ty), textTransform: 'capitalize' }}>
            {ty === 'wfh' ? 'WFH' : ty} ({countOf(ty)})
          </span>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
        {tabs.map((label, i) => (
          <button key={label} type="button" onClick={() => setActiveTab(i as 0 | 1 | 2)}
            style={{ flex: 1, padding: '12px 8px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === i ? 700 : 500, color: activeTab === i ? C.orange : C.charcoalMid, borderBottom: `2px solid ${activeTab === i ? C.orange : 'transparent'}`, fontFamily: 'inherit' }}>
            {label}
          </button>
        ))}
      </div>

      {/* List */}
      <div>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: C.charcoalLight, fontSize: 13 }}>
            {activeTab === 0 ? 'Everyone is in the office today' : activeTab === 1 ? 'No absences planned for this week' : 'No planned absences'}
          </div>
        ) : filtered.map((m, i) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 24px', borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
            {m.avatarUrl ? (
              <img src={m.avatarUrl} alt={m.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.orange, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>{initials(m.name)}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.charcoal }}>{m.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: `${typeColor(C, m.absence.type)}22`, color: typeColor(C, m.absence.type), textTransform: 'uppercase' }}>{m.absence.type === 'wfh' ? 'WFH' : m.absence.type}</span>
                {m.absence.status === 'pending' ? <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: C.yellowLight, color: C.yellowText }}>Pending</span> : null}
              </div>
              <p style={{ fontSize: 12, color: C.charcoalLight, marginTop: 2 }}>{[m.department, fmtRange(m.absence.startDate, m.absence.endDate)].filter(Boolean).join(' · ')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamAbsences;
